import { useEffect, useRef, useState } from "react";
import { motion, useInView, useScroll, useTransform, AnimatePresence } from "framer-motion";
import type { Product } from "@/data/productsData";
import { useTranslation } from 'react-i18next';

interface Props {
  product: Product;
  index: number;
}

// 個別プロダクト・ショーケース。
// Anthropic frontend-design SKILL を参考に AI default (暖クリーム/黒+アシッドグリーン/新聞風) を回避。
// 既存 PLANNINGJOY の dark navy 基調を継承しつつ、プロダクトごとの accent gradient + 3D tilt + carousel。
export const ProductShowcase = ({ product, index }: Props) => {
  const { t, i18n } = useTranslation();
  const isEn = !i18n.language.startsWith('ja');
  const displayName = isEn && product.nameEn ? product.nameEn : product.name;
  const displayTagline = isEn && product.taglineEn ? product.taglineEn : product.tagline;
  const displayDescription = isEn && product.descriptionEn ? product.descriptionEn : product.description;
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-15%" });
  const [activeImage, setActiveImage] = useState(0);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  // 画像 carousel: 4 秒ごとに自動切替
  useEffect(() => {
    if (!inView) return;
    const t = setInterval(() => {
      setActiveImage((prev) => (prev + 1) % product.images.length);
    }, 4200);
    return () => clearInterval(t);
  }, [inView, product.images.length]);

  // mouse-tilt 3D
  const handleMouse = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 8;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -8;
    setTilt({ x, y });
  };
  const handleLeave = () => setTilt({ x: 0, y: 0 });

  const reverse = index % 2 === 1;

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="relative py-16 md:py-24 px-4 md:px-8 overflow-hidden"
    >
      {/* accent gradient background */}
      <div
        className={`absolute inset-0 -z-10 opacity-60 blur-3xl bg-gradient-to-br ${product.accent}`}
        style={{ filter: "blur(80px)" }}
      />

      <div className={`max-w-7xl mx-auto grid lg:grid-cols-12 gap-8 lg:gap-12 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
        {/* テキスト側 */}
        <div className="lg:col-span-5 space-y-6">
          <motion.div
            initial={{ x: reverse ? 40 : -40, opacity: 0 }}
            animate={inView ? { x: 0, opacity: 1 } : {}}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="flex items-center gap-3"
          >
            <span className="font-mono text-xs text-white/40 tracking-[0.2em]">
              {String(index + 1).padStart(2, "0")} / {String(4).padStart(2, "0")}
            </span>
            <span className="h-px flex-1 bg-gradient-to-r from-white/40 to-transparent" />
            <span className="font-mono text-xs text-white/60">{product.year}</span>
          </motion.div>

          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            animate={inView ? { y: 0, opacity: 1 } : {}}
            transition={{ delay: 0.25, duration: 0.8 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.1]"
          >
            {displayName}
          </motion.h2>

          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={inView ? { y: 0, opacity: 1 } : {}}
            transition={{ delay: 0.35, duration: 0.8 }}
            className="text-lg text-white/70 font-medium"
          >
            {displayTagline}
          </motion.p>

          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={inView ? { y: 0, opacity: 1 } : {}}
            transition={{ delay: 0.45, duration: 0.8 }}
            className="text-sm md:text-base text-white/55 leading-relaxed"
          >
            {displayDescription}
          </motion.p>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={inView ? { y: 0, opacity: 1 } : {}}
            transition={{ delay: 0.55, duration: 0.8 }}
            className="flex flex-wrap gap-2"
          >
            {product.stack.map((tech, i) => (
              <span
                key={tech}
                className="text-xs font-mono px-3 py-1 rounded-full backdrop-blur-md bg-white/5 border border-white/10 text-white/75 hover:bg-white/10 transition-colors"
                style={{ animationDelay: `${i * 50}ms` }}
              >
                {tech}
              </span>
            ))}
          </motion.div>
        </div>

        {/* 画像 carousel + 3D tilt */}
        <div className="lg:col-span-7 relative">
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }}
            animate={inView ? { scale: 1, opacity: 1 } : {}}
            transition={{ delay: 0.3, duration: 0.9 }}
            onMouseMove={handleMouse}
            onMouseLeave={handleLeave}
            style={{
              transform: `perspective(1200px) rotateY(${tilt.x}deg) rotateX(${tilt.y}deg)`,
              transition: "transform 0.18s ease-out",
            }}
            className="relative aspect-[16/10] rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/60"
          >
            {/* メイン画像 (フェード切替) */}
            <AnimatePresence mode="sync">
              <motion.img
                key={activeImage}
                src={product.images[activeImage]}
                alt={`${displayName} - screenshot ${activeImage + 1}`}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            </AnimatePresence>
            {/* グラデ overlay */}
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-black/30 via-transparent to-white/5" />
            {/* image index marker */}
            <div className="absolute bottom-4 right-4 font-mono text-xs text-white/70 bg-black/40 backdrop-blur-md px-2 py-1 rounded">
              {activeImage + 1} / {product.images.length}
            </div>
          </motion.div>

          {/* サムネイル dots */}
          <div className="flex gap-2 justify-center mt-4">
            {product.images.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveImage(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === activeImage
                    ? "w-8 bg-white/80"
                    : "w-1.5 bg-white/30 hover:bg-white/50"
                }`}
                aria-label={t('products.screenshot_aria', { n: i + 1 })}
              />
            ))}
          </div>
        </div>
      </div>
    </motion.section>
  );
};
